/*
 * The vocab-flashcards page: a vocab list as a deck of flashcards, the word
 * on the front and the definition on the back, studied under the Facecards
 * app's spaced-repetition scheduler (modules/spaced-repetition.ts). Three
 * modes, picked on the landing (and kept in the URL hash so a reload
 * resumes the same mode):
 *
 *   learn   every card starts unknown; missed cards come back within a few
 *           cards and the gaps stretch as they're learned, until every card
 *           has graduated
 *   review  the same engine assuming the cards are already known: a
 *           perfect run is one shuffled pass, a miss drops that card into
 *           the learn schedule
 *   browse  the cards one at a time with both sides showing, to read
 *           through
 *
 * A session: the card shows its front; any input (a click or tap on the
 * card, the Show button, a key) reveals the back; then Missed / Got it —
 * the buttons, ← / →, or a swipe left / right — grades it and the engine
 * picks the next card. The retired-card count is the progress bar at the
 * bottom, with Start over; the done screen offers another run and a drill
 * of just the missed cards. Progress (which cards have graduated) is kept
 * per deck and mode in localStorage, so a session can be left and resumed
 * — best-effort, wrapped in try/catch, and never read back by anything.
 * Nothing is sent to the server: there is no run to record.
 */

import { must } from './modules/dom.ts';
import { bootstrapFailed, reportErrors } from './modules/fetch-helpers.ts';
import { type LearnState, type Mode, makeState } from './modules/spaced-repetition.ts';

interface Term {
  word: string;
  definition: string;
}

type View = Mode | 'browse';

const el = (tag: string, className?: string, text?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text !== undefined) e.textContent = text;
  return e;
};

const button = (label: string, onClick: () => void, className?: string): HTMLButtonElement => {
  const b = document.createElement('button');
  b.type = 'button';
  b.textContent = label;
  if (className) b.className = className;
  b.addEventListener('click', reportErrors(label, onClick));
  return b;
};

/* Saved progress: the words of the cards retired, per deck and mode. */
const loadRetired = (key: string): string[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((w) => typeof w === 'string') : [];
  } catch {
    return [];
  }
};
const saveRetired = (key: string, words: string[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(words));
  } catch {
    /* storage blocked or full — progress just isn't saved */
  }
};

const buildCard = (t: Term): HTMLElement => {
  const card = el('div', 'card');
  card.appendChild(el('div', 'front', t.word));
  const back = el('div', 'back', t.definition);
  back.hidden = true;
  card.appendChild(back);
  return card;
};

interface Session {
  teardown(): void;
}

interface StudyOpts {
  title: string;
  onAnswer?: () => void;
  onRestart: () => void;
  restartLabel: string;
  onStudyMissed: (mode: Mode, missed: Term[]) => void;
  onHome: () => void;
}

/* The study driver (Facecards' runStudy): one card at a time, reveal then
 * grade, the engine choosing the order. */
const runStudy = (
  state: LearnState<Term>,
  stage: HTMLElement,
  footer: HTMLElement,
  opts: StudyOpts,
): Session => {
  let current: Term | null = null;
  let cardEl: HTMLElement | null = null;
  let revealed = false;

  stage.replaceChildren();
  const header = el('h2', 'study-header', opts.title);
  const currentEl = el('div', 'current');
  const controls = el('div', 'controls');
  stage.append(header, currentEl, controls);

  // The retired-cards progress bar.
  footer.replaceChildren();
  const track = el('div', 'track');
  const fill = el('div', 'fill');
  track.appendChild(fill);
  const count = el('span', 'count');
  footer.append(track, count, button('Start over', opts.onRestart, 'reset'));
  footer.hidden = false;

  const updateProgress = () => {
    const retired = state.retired();
    fill.style.width = `${state.total ? (100 * retired) / state.total : 0}%`;
    count.textContent = `${retired} / ${state.total}`;
  };

  const showControls = () => {
    controls.replaceChildren();
    if (!current) return;
    if (!revealed) {
      controls.appendChild(button('Show definition', reveal, 'primary'));
    } else {
      controls.append(
        button('Missed', () => grade(false), 'missed'),
        button('Got it', () => grade(true), 'got-it'),
      );
    }
  };

  const advance = () => {
    updateProgress();
    current = state.next();
    revealed = false;
    if (current === null) {
      cardEl = null;
      const done = el('div', 'study-done');
      const perfect = state.missed.length === 0;
      done.appendChild(
        el(
          'p',
          'message',
          state.trial === 0
            ? 'Every card is already learned!'
            : `Done!${perfect ? ' Perfect run!' : ''}`,
        ),
      );
      const buttons = el('div', 'done-buttons');
      buttons.appendChild(button(opts.restartLabel, opts.onRestart));
      if (!perfect) {
        buttons.append(
          button('Learn missed', () => opts.onStudyMissed('learn', state.missed.slice())),
          button('Review missed', () => opts.onStudyMissed('review', state.missed.slice())),
        );
      }
      buttons.appendChild(button('Back', opts.onHome));
      done.appendChild(buttons);
      currentEl.replaceChildren(done);
      controls.replaceChildren();
      return;
    }
    cardEl = buildCard(current);
    cardEl.addEventListener('click', () => reveal());
    currentEl.replaceChildren(cardEl);
    showControls();
  };

  const reveal = () => {
    if (!current || !cardEl || revealed) return;
    revealed = true;
    const back = cardEl.querySelector<HTMLElement>('.back');
    if (back) back.hidden = false;
    cardEl.classList.add('revealed');
    showControls();
  };

  const grade = (gotIt: boolean) => {
    if (!current || !revealed) return;
    if (gotIt) state.correct(current);
    else state.incorrect(current);
    opts.onAnswer?.();
    advance();
  };

  // Keys: ← missed, → got it (once revealed); anything else reveals.
  const onKeyDown = (e: KeyboardEvent) => {
    if (!current) return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape' || e.key === 'Tab') return;
    if (e.target instanceof HTMLButtonElement && (e.key === 'Enter' || e.key === ' ')) {
      return; // let the focused button take its own click
    }
    if (revealed && e.key === 'ArrowLeft') grade(false);
    else if (revealed && e.key === 'ArrowRight') grade(true);
    else if (!revealed) reveal();
    else return;
    e.preventDefault();
  };

  // Swipes: left = missed, right = got it (once revealed); a tap reveals.
  let touchStart: number | null = null;
  const onTouchStart = (e: TouchEvent) => {
    touchStart = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStart === null) return;
    const end = e.changedTouches[0]?.clientX ?? touchStart;
    const dx = end - touchStart;
    touchStart = null;
    if (!revealed) return; // the tap itself reveals via the card's click
    if (Math.abs(dx) > 50) grade(dx > 0);
  };

  window.addEventListener('keydown', onKeyDown);
  currentEl.addEventListener('touchstart', onTouchStart, { passive: true });
  currentEl.addEventListener('touchend', onTouchEnd);

  advance();

  return {
    teardown() {
      window.removeEventListener('keydown', onKeyDown);
      currentEl.removeEventListener('touchstart', onTouchStart);
      currentEl.removeEventListener('touchend', onTouchEnd);
      footer.hidden = true;
      footer.replaceChildren();
    },
  };
};

/* Browse: the deck in order, both sides showing, ← / → or swipe to move. */
const runBrowse = (
  terms: Term[],
  stage: HTMLElement,
  footer: HTMLElement,
  title: string,
  onHome: () => void,
): Session => {
  let i = 0;
  stage.replaceChildren();
  const header = el('h2', 'study-header', title);
  const currentEl = el('div', 'current');
  const controls = el('div', 'controls');
  stage.append(header, currentEl, controls);
  footer.replaceChildren();
  const count = el('span', 'count');
  footer.append(count, button('Back', onHome, 'reset'));
  footer.hidden = false;

  const show = () => {
    const card = buildCard(terms[i]);
    const back = card.querySelector<HTMLElement>('.back');
    if (back) back.hidden = false;
    card.classList.add('revealed');
    currentEl.replaceChildren(card);
    count.textContent = `${i + 1} / ${terms.length}`;
    controls.replaceChildren(
      button('Previous', () => move(-1), ''),
      button('Next', () => move(1), 'primary'),
    );
  };
  const move = (d: number) => {
    i = (i + d + terms.length) % terms.length;
    show();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'ArrowLeft') move(-1);
    else if (e.key === 'ArrowRight' || e.key === ' ') move(1);
    else return;
    e.preventDefault();
  };
  let touchStart: number | null = null;
  const onTouchStart = (e: TouchEvent) => {
    touchStart = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStart === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStart) - touchStart;
    touchStart = null;
    if (Math.abs(dx) > 50) move(dx > 0 ? -1 : 1);
  };
  window.addEventListener('keydown', onKeyDown);
  currentEl.addEventListener('touchstart', onTouchStart, { passive: true });
  currentEl.addEventListener('touchend', onTouchEnd);
  show();
  return {
    teardown() {
      window.removeEventListener('keydown', onKeyDown);
      currentEl.removeEventListener('touchstart', onTouchStart);
      currentEl.removeEventListener('touchend', onTouchEnd);
      footer.hidden = true;
      footer.replaceChildren();
    },
  };
};

const MODE_TITLES: Record<View, string> = {
  learn: 'Learn',
  review: 'Review',
  browse: 'Browse',
};

const setup = async () => {
  const root = must(document.getElementById('vocab-flashcards'), '#vocab-flashcards');
  const terms: Term[] = JSON.parse(
    must(document.getElementById('vocab-terms'), '#vocab-terms').textContent ?? '[]',
  );
  const storeBase = root.dataset.store ?? 'vocab-flashcards';
  const landing = must(document.getElementById('landing'), '#landing');
  const stage = must(document.getElementById('stage'), '#stage');
  const footer = must(document.getElementById('study-progress'), '#study-progress');

  let session: Session | null = null;
  const clear = () => {
    session?.teardown();
    session = null;
  };

  const showLanding = () => {
    clear();
    stage.hidden = true;
    landing.hidden = false;
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
    // The per-mode resume notes: how many cards are already retired.
    for (const mode of ['learn', 'review'] as const) {
      const note = landing.querySelector<HTMLElement>(`[data-saved="${mode}"]`);
      if (!note) continue;
      const retired = loadRetired(`${storeBase}:${mode}`).filter((w) =>
        terms.some((t) => t.word === w),
      ).length;
      note.textContent = retired ? `${retired} of ${terms.length} learned so far` : '';
    }
  };

  // missed, if given, is a one-off drill of those cards: no saved progress
  // is read or written.
  const showStudy = (view: View, missed?: Term[]) => {
    clear();
    landing.hidden = true;
    stage.hidden = false;
    if (!missed) history.replaceState(null, '', `#${view}`);
    window.scrollTo(0, 0);
    const deck = missed ?? terms;
    const title = `${MODE_TITLES[view]}${missed ? ' — missed' : ''}`;

    if (view === 'browse') {
      session = runBrowse(terms, stage, footer, title, showLanding);
      return;
    }

    const state = makeState(view, deck);
    const storeKey = missed ? null : `${storeBase}:${view}`;
    const retired = new Set(storeKey ? loadRetired(storeKey) : []);
    for (const t of deck) if (retired.has(t.word)) state.restore(t);

    session = runStudy(state, stage, footer, {
      title,
      onAnswer: storeKey
        ? () => {
            // Re-sync this deck's cards into the saved set (a miss can
            // un-retire a card, so removal matters as much as addition).
            for (const t of deck) {
              if (state.graduated.includes(t)) retired.add(t.word);
              else retired.delete(t.word);
            }
            saveRetired(storeKey, [...retired]);
          }
        : undefined,
      onRestart: () => {
        if (storeKey) saveRetired(storeKey, []);
        showStudy(view, missed);
      },
      restartLabel: view === 'review' ? 'Review again' : 'Learn again',
      onStudyMissed: (mode, missedCards) => showStudy(mode, missedCards),
      onHome: showLanding,
    });
  };

  for (const b of landing.querySelectorAll<HTMLButtonElement>('button[data-mode]')) {
    const mode = b.dataset.mode as View;
    b.addEventListener(
      'click',
      reportErrors(`start ${mode}`, () => showStudy(mode)),
    );
  }

  const initial = location.hash.replace(/^#/, '');
  if (initial === 'learn' || initial === 'review' || initial === 'browse') showStudy(initial);
  else showLanding();
};

setup().catch(bootstrapFailed);
